import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import './Register.css';

interface RegisterForm {
  imei: string;
  first_name: string;
  email: string;
  password: string;
  phone: string;
}

export default function Register() {
  const [params] = useSearchParams();
  const imeiFromUrl = params.get('imei') || '';
  const hasImei = imeiFromUrl.length > 0;

  const [form, setForm] = useState<RegisterForm>({
    imei: imeiFromUrl,
    first_name: '',
    email: '',
    password: '',
    phone: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [phase, setPhase] = useState<'form' | 'sent'>('form');
  const [verifyUrl, setVerifyUrl] = useState('');
  const [sentEmail, setSentEmail] = useState('');

  // Resend cooldown
  const [cooldown, setCooldown] = useState(0);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState('');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startCooldown = (seconds = 60) => {
    setCooldown(seconds);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) {
          clearInterval(timerRef.current!);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  };

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.imei.trim()) { setError('Podaj IMEI urządzenia'); return; }
    if (form.password.length < 8) { setError('Hasło musi mieć co najmniej 8 znaków'); return; }
    setLoading(true);
    setError('');
    try {
      const r = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imei: form.imei.trim(),
          email: form.email,
          password: form.password,
          first_name: form.first_name,
          phone: form.phone || undefined,
        }),
      });
      const data = await r.json();
      if (!r.ok) { setError(data.detail || 'Błąd rejestracji'); return; }
      setSentEmail(form.email);
      setVerifyUrl(data.verification_url || '');
      setPhase('sent');
      startCooldown(60);
    } catch {
      setError('Błąd połączenia. Spróbuj ponownie.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setResendMsg('');
    try {
      const r = await fetch('/api/auth/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: sentEmail }),
      });
      const data = await r.json();
      if (!r.ok) {
        setResendMsg(data.detail || 'Błąd');
      } else {
        setResendMsg('Wysłano nowy link!');
        if (data.verification_url) setVerifyUrl(data.verification_url);
        startCooldown(60);
      }
    } catch {
      setResendMsg('Błąd połączenia');
    } finally {
      setResending(false);
    }
  };

  if (phase === 'sent') {
    return (
      <div className="reg-page">
        <div className="reg-card">
          <div className="reg-logo">
            <span className="logo-10">10</span><span className="logo-drive">drive</span>
            <span className="logo-moto">moto</span>
          </div>
          <div className="reg-success">
            <div className="reg-icon">✉</div>
            <h2>Sprawdź skrzynkę email</h2>
            <p className="reg-hint">
              Wysłaliśmy link aktywacyjny na adres<br />
              <strong>{sentEmail}</strong>
            </p>
            <p className="reg-hint">Kliknij link w emailu, aby aktywować konto.</p>
          </div>

          {verifyUrl && (
            <div className="reg-dev-link">
              <p className="reg-dev-label">Link aktywacyjny (dev):</p>
              <a href={verifyUrl} className="reg-verify-link">{verifyUrl}</a>
            </div>
          )}

          <div className="reg-resend">
            {resendMsg && <p className={`reg-resend-msg ${resendMsg.includes('Wysłano') ? 'ok' : 'err'}`}>{resendMsg}</p>}
            {cooldown > 0
              ? <p className="reg-cooldown">Wyślij ponownie za {cooldown}s</p>
              : (
                <button className="reg-btn-secondary" onClick={handleResend} disabled={resending}>
                  {resending ? 'Wysyłanie...' : 'Wyślij link ponownie'}
                </button>
              )}
          </div>

          <Link to="/login" className="reg-link-back">Mam już konto — zaloguj się</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="reg-page">
      <div className="reg-card">
        <div className="reg-logo">
          <span className="logo-10">10</span><span className="logo-drive">drive</span>
          <span className="logo-moto">moto</span>
        </div>
        <h2>Rejestracja urządzenia</h2>

        {!hasImei && (
          <div className="reg-scan-hint">
            <p>Zeskanuj kod QR z naklejki na urządzeniu lub wpisz numer IMEI poniżej.</p>
          </div>
        )}

        <form className="reg-form" onSubmit={handleSubmit}>
          <div className="reg-field">
            <label>Numer IMEI urządzenia</label>
            <input
              name="imei"
              type="text"
              inputMode="numeric"
              placeholder="356938035643809"
              value={form.imei}
              onChange={handleChange}
              readOnly={hasImei}
              className={hasImei ? 'readonly' : ''}
              autoComplete="off"
            />
          </div>

          <div className="reg-field">
            <label>Imię</label>
            <input name="first_name" type="text" placeholder="Jan" autoComplete="given-name"
              value={form.first_name} onChange={handleChange} required />
          </div>

          <div className="reg-field">
            <label>Adres email</label>
            <input name="email" type="email" placeholder="jan@example.com" autoComplete="email"
              value={form.email} onChange={handleChange} required />
          </div>

          <div className="reg-field">
            <label>Hasło (min. 8 znaków)</label>
            <input name="password" type="password" placeholder="hasło" autoComplete="new-password"
              value={form.password} onChange={handleChange} required />
          </div>

          <div className="reg-field">
            <label>Telefon <span className="reg-label-hint">(opcjonalnie — potrzebny do alertów SMS)</span></label>
            <input name="phone" type="tel" placeholder="+48 600 000 000" autoComplete="tel"
              value={form.phone} onChange={handleChange} />
          </div>

          {error && <p className="reg-error">{error}</p>}

          <button type="submit" className="reg-btn" disabled={loading}>
            {loading ? 'Rejestracja...' : 'Zarejestruj urządzenie'}
          </button>
        </form>

        <Link to="/login" className="reg-link-back">Mam już konto — zaloguj się</Link>
      </div>
    </div>
  );
}
